import { useState, useEffect, type FormEvent, type ChangeEvent } from 'react';
import { FiUser, FiLock, FiShield, FiRefreshCw, FiMoon, FiSun } from 'react-icons/fi';
import QRCode from 'qrcode';
import { cpApi } from '../api/api';
import {
  isAutoRefreshEnabled,
  getAutoRefreshInterval,
  setAutoRefreshPreferences,
} from '../utils/preferences';
import { useTheme } from '../hooks/useTheme';
import type { Counterparty } from '../types';

const emitToast = (message: string, type: 'success' | 'error' = 'success') => {
  window.dispatchEvent(new CustomEvent('showToast', { detail: { message, type } }));
};

const getApiError = (err: unknown): string => {
  if (typeof err === 'string') return err;
  const e = err as { response?: { data?: string | { error?: string; message?: string } }; message?: string };
  const data = e?.response?.data;
  if (typeof data === 'string') return data;
  if (data && typeof data === 'object') return (data as { error?: string }).error || (data as { message?: string }).message || 'Ошибка';
  return e?.message || 'Ошибка';
};

export default function SettingsPage() {
  const { toggleTheme, isDark } = useTheme();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [profile, setProfile] = useState<Counterparty | null>(null);
  const [formData, setFormData] = useState({ name: '', login: '', currentPassword: '', newPassword: '', confirmPassword: '' });

  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(false);
  const [autoRefreshInterval, setAutoRefreshInterval] = useState(60);
  const [autoRefreshSaving, setAutoRefreshSaving] = useState(false);
  const [autoRefreshSuccess, setAutoRefreshSuccess] = useState<string | null>(null);
  const [themeSaving, setThemeSaving] = useState(false);
  const [themeSuccess, setThemeSuccess] = useState<string | null>(null);

  const [isEditing2FA, setIsEditing2FA] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [isVerifying2FA, setIsVerifying2FA] = useState(false);
  const [isDisabling2FA, setIsDisabling2FA] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [disable2FACode, setDisable2FACode] = useState('');
  const [showSuccess2FA, setShowSuccess2FA] = useState(false);
  const [showCopyNotification, setShowCopyNotification] = useState(false);

  useEffect(() => {
    const loadProfile = async () => {
      try {
        const p = await cpApi.getProfile();
        setProfile(p);
        setFormData((prev) => ({ ...prev, name: p.name || '', login: p.login || '' }));
      } catch {
        setError('Ошибка при загрузке профиля');
      } finally {
        setLoading(false);
      }
    };
    setAutoRefreshEnabled(isAutoRefreshEnabled());
    setAutoRefreshInterval(getAutoRefreshInterval());
    loadProfile();
  }, []);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAutoRefreshToggle = () => {
    setAutoRefreshEnabled((prev) => !prev);
    setAutoRefreshSaving(true);
    try {
      setAutoRefreshPreferences(!autoRefreshEnabled, autoRefreshInterval);
      setAutoRefreshSuccess('Настройка сохранена');
      setTimeout(() => setAutoRefreshSuccess(null), 2000);
    } finally {
      setAutoRefreshSaving(false);
    }
  };

  const handleAutoRefreshIntervalChange = (value: number) => {
    setAutoRefreshInterval(Math.max(10, Math.min(300, Math.round(value))));
  };

  const handleAutoRefreshApply = () => {
    setAutoRefreshSaving(true);
    try {
      setAutoRefreshPreferences(autoRefreshEnabled, autoRefreshInterval);
      setAutoRefreshSuccess('Настройка сохранена');
      setTimeout(() => setAutoRefreshSuccess(null), 2000);
      emitToast('Настройки сохранены');
    } finally {
      setAutoRefreshSaving(false);
    }
  };

  const handleThemeToggle = () => {
    setThemeSaving(true);
    try {
      toggleTheme();
      setThemeSuccess('Тема изменена');
      setTimeout(() => setThemeSuccess(null), 2000);
      emitToast('Тема изменена');
    } finally {
      setThemeSaving(false);
    }
  };

  const handle2FAToggle = async () => {
    if (profile?.twofa_check) {
      setIsDisabling2FA(true);
      setDisable2FACode('');
      setError(null);
    } else {
      try {
        setSaving(true);
        setError(null);
        const res = await cpApi.setup2FA();
        const data = res as { qr_code_url?: string; backup_codes?: string[] };
        setQrCodeUrl(data.qr_code_url || '');
        setBackupCodes(data.backup_codes || []);
        if (data.qr_code_url) {
          const url = await QRCode.toDataURL(data.qr_code_url);
          setQrCodeDataUrl(url);
        }
        setIsEditing2FA(true);
      } catch {
        setError('Ошибка при настройке 2FA');
        emitToast('Ошибка настройки 2FA', 'error');
      } finally {
        setSaving(false);
      }
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto w-full">
      <div className="p-6 w-full">
        <form onSubmit={(e: FormEvent) => e.preventDefault()} className="space-y-6">
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg dark:bg-red-950/35 dark:border-red-900/50 dark:text-red-200">
              {error}
            </div>
          )}
          {success && (
            <div className="p-4 bg-green-50 border border-green-200 text-green-700 rounded-lg dark:bg-green-900/20 dark:border-green-800 dark:text-green-300">
              {success}
            </div>
          )}

          {profile && !profile.twofa_check && (
            <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded-xl mb-6">
              <p className="font-medium text-amber-800 dark:text-amber-200">
                Для доступа к кабинету необходимо включить двухфакторную аутентификацию (2FA).
              </p>
              <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                Включите 2FA в блоке ниже.
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 w-full">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                  <FiUser className="w-5 h-5 text-blue-600 dark:text-blue-300" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Основная информация</h3>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Имя</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-900/30 text-gray-500 cursor-not-allowed"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Логин</label>
                  <input
                    type="text"
                    name="login"
                    value={formData.login}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900/30 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500"
                    placeholder="Введите логин"
                  />
                </div>
                <div className="flex justify-end pt-2">
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        setSaving(true);
                        setError(null);
                        await cpApi.updateProfile({ login: formData.login });
                        setSuccess('Логин успешно обновлён');
                        setTimeout(() => setSuccess(null), 3000);
                        emitToast('Логин обновлён');
                      } catch (err) {
                        setError(getApiError(err));
                      } finally {
                        setSaving(false);
                      }
                    }}
                    disabled={saving || !formData.login}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    {saving ? 'Сохранение...' : 'Сохранить логин'}
                  </button>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
                  <FiLock className="w-5 h-5 text-green-600 dark:text-green-300" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Смена пароля</h3>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Текущий пароль</label>
                  <input
                    type="password"
                    name="currentPassword"
                    value={formData.currentPassword}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900/30"
                    placeholder="Введите текущий пароль"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Новый пароль</label>
                  <input
                    type="password"
                    name="newPassword"
                    value={formData.newPassword}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900/30"
                    placeholder="Введите новый пароль"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Подтвердите пароль</label>
                  <input
                    type="password"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900/30"
                    placeholder="Подтвердите новый пароль"
                  />
                </div>
                <div className="flex justify-end pt-2">
                  <button
                    type="button"
                    onClick={async () => {
                      if (formData.newPassword !== formData.confirmPassword) {
                        setError('Пароли не совпадают');
                        return;
                      }
                      if (formData.newPassword.length < 6) {
                        setError('Пароль должен содержать минимум 6 символов');
                        return;
                      }
                      try {
                        setSaving(true);
                        setError(null);
                        await cpApi.updateProfile({ password: formData.newPassword });
                        setSuccess('Пароль успешно обновлён');
                        setTimeout(() => setSuccess(null), 3000);
                        setFormData((p) => ({ ...p, currentPassword: '', newPassword: '', confirmPassword: '' }));
                        emitToast('Пароль обновлён');
                      } catch (err) {
                        setError(getApiError(err));
                      } finally {
                        setSaving(false);
                      }
                    }}
                    disabled={saving || !formData.newPassword || !formData.confirmPassword}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                  >
                    {saving ? 'Сохранение...' : 'Сохранить пароль'}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Автообновление */}
          <div className="mt-6 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                <FiRefreshCw className="w-5 h-5 text-blue-600 dark:text-blue-300" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Автообновление заявок</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">При включении заявки обновляются автоматически</p>
              </div>
            </div>
            {autoRefreshSuccess && (
              <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-300 rounded-lg text-sm">
                {autoRefreshSuccess}
              </div>
            )}
            <div className="space-y-4">
              <div className="flex items-center justify-between bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                <div>
                  <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
                    {autoRefreshEnabled ? `Обновление каждые ${autoRefreshInterval} сек.` : 'Автообновление выключено.'}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleAutoRefreshToggle}
                  disabled={autoRefreshSaving}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-gray-800 ${
                    autoRefreshEnabled ? 'bg-blue-500 focus:ring-blue-500' : 'bg-gray-300 focus:ring-gray-400'
                  } ${autoRefreshSaving ? 'opacity-60 cursor-not-allowed' : ''}`}
                >
                  <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${autoRefreshEnabled ? 'translate-x-5' : 'translate-x-1'}`} />
                </button>
              </div>
              <div className={`bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg p-4 space-y-4 ${!autoRefreshEnabled ? 'opacity-50 pointer-events-none' : ''}`}>
                <div className="flex justify-between">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Период (сек.)</label>
                  <span className="font-semibold text-gray-900 dark:text-gray-100">{autoRefreshInterval}</span>
                </div>
                <input
                  type="range"
                  min={10}
                  max={300}
                  step={10}
                  value={autoRefreshInterval}
                  onChange={(e) => handleAutoRefreshIntervalChange(Number(e.target.value))}
                  className="w-full"
                />
                <button
                  type="button"
                  onClick={handleAutoRefreshApply}
                  disabled={autoRefreshSaving}
                  className="px-4 py-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {autoRefreshSaving ? 'Сохранение…' : 'Сохранить настройки'}
                </button>
              </div>
            </div>
          </div>

          {/* Тема оформления — как у трейдера */}
          <div className="mt-6 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-10 h-10 bg-indigo-100 dark:bg-indigo-900 rounded-lg flex items-center justify-center">
                {isDark ? (
                  <FiSun className="w-5 h-5 text-indigo-600 dark:text-indigo-300" />
                ) : (
                  <FiMoon className="w-5 h-5 text-indigo-600 dark:text-indigo-300" />
                )}
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Тема оформления</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Переключите между светлой и тёмной темой интерфейса
                </p>
              </div>
            </div>
            {themeSuccess && (
              <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-300 rounded-lg text-sm">
                {themeSuccess}
              </div>
            )}
            <div className="flex items-center justify-between gap-3 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg p-4">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
                  {isDark ? 'Тёмная тема' : 'Светлая тема'}
                </p>
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  {isDark
                    ? 'Интерфейс отображается в тёмной теме для комфортной работы в условиях низкой освещённости.'
                    : 'Интерфейс отображается в светлой теме по умолчанию.'}
                </p>
              </div>
              <button
                type="button"
                onClick={handleThemeToggle}
                disabled={themeSaving}
                className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-gray-800 ${
                  isDark ? 'bg-indigo-500 focus:ring-indigo-500' : 'bg-gray-300 focus:ring-gray-400'
                } ${themeSaving ? 'opacity-60 cursor-not-allowed' : ''}`}
              >
                <span
                  className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                    isDark ? 'translate-x-5' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>

          {/* 2FA */}
          <div className="mt-6 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                <FiShield className="w-5 h-5 text-blue-600 dark:text-blue-300" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Двухфакторная аутентификация (2FA)</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Защита аккаунта</p>
              </div>
            </div>
            <div className="flex items-center justify-between gap-3 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg p-4">
              <p className="text-sm font-medium text-gray-800 dark:text-gray-200 min-w-0 flex-1">
                Статус 2FA: {profile?.twofa_check ? (
                  <span className="text-green-600 dark:text-green-400 font-semibold">Включена</span>
                ) : (
                  <span className="text-red-600 dark:text-red-400 font-semibold">Отключена</span>
                )}
              </p>
              <button
                type="button"
                onClick={handle2FAToggle}
                disabled={saving}
                className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full ${
                  profile?.twofa_check ? 'bg-blue-600' : 'bg-gray-300'
                }`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white ${profile?.twofa_check ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </div>
          </div>
        </form>

        {/* Модалка 2FA включение */}
        {isEditing2FA && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
              <h3 className="text-lg font-semibold mb-4">Настройка 2FA</h3>
              {!isVerifying2FA ? (
                <div className="space-y-4">
                  <p className="text-sm text-gray-600 dark:text-gray-400">Отсканируйте QR-код в Google Authenticator</p>
                  {qrCodeDataUrl && (
                    <div className="flex flex-col items-center mb-4">
                      <img src={qrCodeDataUrl} alt="QR" className="mb-2" />
                      {qrCodeUrl && (
                        <span className="text-xs text-gray-500 break-all">
                          Секрет: <b>{qrCodeUrl.split('secret=')[1]?.split('&')[0]}</b>
                        </span>
                      )}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <button onClick={() => setIsVerifying2FA(true)} className="flex-1 py-2 bg-blue-600 text-white rounded-lg">
                      Продолжить
                    </button>
                    <button
                      onClick={() => { setIsEditing2FA(false); setQrCodeDataUrl(''); setBackupCodes([]); }}
                      className="flex-1 py-2 bg-gray-200 dark:bg-gray-700 rounded-lg"
                    >
                      Отмена
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <p className="text-sm text-gray-600 dark:text-gray-400">Введите 6-значный код:</p>
                  <input
                    type="text"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    maxLength={6}
                    className="w-full px-3 py-2 rounded-lg border dark:bg-gray-900 dark:border-gray-600"
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={async () => {
                        if (!verificationCode.trim()) { setError('Введите код'); return; }
                        try {
                          setSaving(true);
                          setError(null);
                          await cpApi.enable2FA(verificationCode);
                          setShowSuccess2FA(true);
                          setIsEditing2FA(false);
                          setIsVerifying2FA(false);
                          setVerificationCode('');
                          setQrCodeDataUrl('');
                          const p = await cpApi.getProfile();
                          setProfile(p);
                          emitToast('2FA включена');
                        } catch {
                          setError('Неверный код');
                        } finally {
                          setSaving(false);
                        }
                      }}
                      disabled={saving}
                      className="flex-1 py-2 bg-blue-600 text-white rounded-lg disabled:opacity-50"
                    >
                      {saving ? 'Проверка...' : 'Подтвердить'}
                    </button>
                    <button onClick={() => setIsVerifying2FA(false)} className="flex-1 py-2 bg-gray-200 dark:bg-gray-700 rounded-lg">
                      Назад
                    </button>
                  </div>
                </div>
              )}
              {error && <p className="mt-2 text-sm text-red-500">{error}</p>}
            </div>
          </div>
        )}

        {/* Модалка успеха 2FA с кодами */}
        {showSuccess2FA && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
              <div className="text-center mb-4">
                <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium mb-2">2FA включена!</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">Сохраните резервные коды в безопасном месте.</p>
              </div>
              <div className="bg-gray-50 dark:bg-gray-900/30 p-4 rounded border mb-4">
                <h4 className="text-sm font-medium mb-2">Резервные коды:</h4>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  {backupCodes.map((code, i) => (
                    <div key={i} className="bg-white dark:bg-gray-800 px-2 py-1 rounded text-center font-mono">
                      {code}
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex gap-2 mb-4">
                <button
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(backupCodes.join('\n'));
                      setShowCopyNotification(true);
                      setTimeout(() => setShowCopyNotification(false), 2000);
                    } catch { /* clipboard may fail in some contexts */ }
                  }}
                  className="flex-1 py-2 bg-blue-600 text-white rounded-lg text-sm"
                >
                  📋 Копировать
                </button>
                <button
                  onClick={() => {
                    const blob = new Blob([backupCodes.join('\n')], { type: 'text/plain' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `wt_2fa_${new Date().toISOString().slice(0, 10)}.txt`;
                    a.click();
                    URL.revokeObjectURL(url);
                  }}
                  className="flex-1 py-2 bg-green-600 text-white rounded-lg text-sm"
                >
                  💾 Скачать
                </button>
              </div>
              {showCopyNotification && (
                <div className="mb-4 p-2 bg-green-50 border border-green-200 rounded text-center text-sm text-green-700">
                  Коды скопированы!
                </div>
              )}
              <button
                onClick={async () => {
                  setShowSuccess2FA(false);
                  setBackupCodes([]);
                  const p = await cpApi.getProfile();
                  setProfile(p);
                }}
                className="w-full py-2 bg-blue-600 text-white rounded-lg"
              >
                Закрыть
              </button>
            </div>
          </div>
        )}

        {/* Модалка отключения 2FA */}
        {isDisabling2FA && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 max-w-md w-full">
              <h3 className="text-lg font-semibold mb-4">Отключить 2FA</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">Введите код 2FA или резервный код:</p>
              <input
                type="text"
                value={disable2FACode}
                onChange={(e) => setDisable2FACode(e.target.value)}
                placeholder="Введите код"
                maxLength={8}
                className="w-full px-3 py-2 rounded-lg border dark:bg-gray-900 dark:border-gray-600 mb-4"
              />
              {error && <p className="mb-4 text-sm text-red-500">{error}</p>}
              <div className="flex gap-2">
                <button
                  onClick={async () => {
                    if (!disable2FACode.trim()) { setError('Введите код'); return; }
                    try {
                      setSaving(true);
                      setError(null);
                      await cpApi.disable2FA(disable2FACode);
                      const p = await cpApi.getProfile();
                      setProfile(p);
                      setIsDisabling2FA(false);
                      setDisable2FACode('');
                      setSuccess('2FA отключена');
                      setTimeout(() => setSuccess(null), 3000);
                      emitToast('2FA отключена');
                    } catch {
                      setError('Неверный код');
                    } finally {
                      setSaving(false);
                    }
                  }}
                  disabled={saving}
                  className="flex-1 py-2 bg-red-600 text-white rounded-lg disabled:opacity-50"
                >
                  {saving ? 'Отключение...' : 'Отключить 2FA'}
                </button>
                <button
                  onClick={() => { setIsDisabling2FA(false); setDisable2FACode(''); setError(null); }}
                  className="flex-1 py-2 bg-gray-200 dark:bg-gray-700 rounded-lg"
                >
                  Отмена
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
